import { Location } from "history";
import isFunction from "lodash-es/isFunction";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import Page, { PageRouteProps } from "@/mk2/pages/Page";
import { AppState, getRequestUser } from "@/mk2/reducers";
import { UserEntity } from "@/mk2/schemas";
import { RouteNavigateFunction, redirectInPWA } from "@/mk2/services/browserHistory";
import { Store } from "@/mk2/store/configureStore";
import { isAuthenticated } from "@/mk/bazaar/common/userUtils";

interface OwnProps {}
interface StateProps {
    requestUser: UserEntity;
}
interface DispatchProps {}
type RouteProps<P extends { [K in keyof P]?: string }> = PageRouteProps<P>;

type Props<P extends { [K in keyof P]?: string }> = OwnProps &
    StateProps &
    DispatchProps &
    RouteProps<P>;

export type RedirectDefinition<P extends { [K in keyof P]?: string }> =
    | string
    | ((p: Props<P>) => string);

export interface RedirectOptions<P extends { [K in keyof P]?: string }> {
    to: RedirectDefinition<P>;
    permanent?: boolean;
    authOnly?: boolean;
}

export default class RedirectPage<P extends { [K in keyof P]?: string }> extends Page<
    OwnProps,
    StateProps,
    DispatchProps
> {
    private options: RedirectOptions<P>;

    constructor(options: RedirectOptions<P>) {
        super();

        this.options = options;
    }

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps<P>, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps<P>): StateProps => ({
            requestUser: getRequestUser(state),
        });
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {};
    }

    public getPermissionRequired(props: Props<P>): boolean {
        const { requestUser } = props;
        const { authOnly } = this.options;
        return !authOnly || isAuthenticated(requestUser);
    }

    public getMetaTitle(props: Props<P>): string {
        return "";
    }

    public hasHeaderBackButton(props: Props<P>): boolean {
        return false;
    }

    public render(props: Props<P>): false | JSX.Element {
        const { requestUser, routeNavigate, location } = props;
        const { to, permanent } = this.options;
        const gotoUrl = isFunction(to) ? to(props) : to;

        return (
            <RedirectTo
                routeNavigate={routeNavigate}
                location={location}
                gotoUrl={gotoUrl}
                permanent={permanent}
                requestUser={requestUser}
            />
        );
    }

    public registerInStore(store: Store<AppState>) {
        // nothing to do
    }
}

interface RedirectToProps {
    routeNavigate: RouteNavigateFunction;
    location: Location;
    gotoUrl: string;
    permanent: boolean;
    requestUser: UserEntity;
}

class RedirectTo extends React.Component<RedirectToProps> {
    public UNSAFE_componentWillMount() {
        const { routeNavigate, gotoUrl, permanent } = this.props;
        redirectInPWA(routeNavigate, gotoUrl, permanent ? 301 : 302);
    }

    public render() {
        return null;
    }
}
